import { Box, Container, Divider, Grid as MUIGrid, Typography, useTheme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import RectangleIcon from '@mui/icons-material/Rectangle';

import RichText from '../../common/RichText';
import { Alignment, PreTitleVariation } from '../../types/enums';
import { ATCOPaletteColor, ThemeMode } from '../../theme/ATCOPaletteColor';
import { TypographyVariants } from '../../theme/TypographyVariants';
import { getBgColorFromThemeMode, getPreTitleDividerColorFromThemeMode, getPreTitleIconColorFromThemeMode, getTextColorFromTheme } from '../../utils/ATCOUtils';
import useResponsiveHelper from '../common/hooks/useResponsiveHelper';
import useCommonStyles from '../common/useCommontStyle';

type GridProps = {
    childComponents: JSX.Element[];
    placeholderComponent: JSX.Element | null;
    xsSize: number;
    smSize: number;
    mdSize: number;
    lgSize: number;
    itemSizes?: boolean;
    preTitleOptions?: PreTitleVariation;
    preTitle?: string;
    title?: string;
    description?: string;
    titleTypography: TypographyVariants;
    color: ThemeMode;
    orientation: Alignment;
    isOnPageLevel?: boolean;
};

const useStyles = makeStyles((theme) => ({
    content: {
        paddingTop: '48px',
        paddingBottom: '48px',
        [theme.breakpoints.down('tablet')]: {
            paddingTop: '40px',
            paddingBottom: '40px',
        },
    },
}));

const Grid: React.FC<GridProps> = (props: GridProps) => {
    const { childComponents, placeholderComponent, xsSize, smSize, mdSize, lgSize, itemSizes, preTitleOptions, preTitle, title, description, titleTypography, color, orientation, isOnPageLevel } =
        props;
    const theme = useTheme();
    const classes = useStyles();
    const { containerWidth } = useResponsiveHelper();
    const commonClasses = useCommonStyles(theme);

    return (
        <Box sx={{ backgroundColor: getBgColorFromThemeMode(color) }}>
            <Container
                maxWidth={containerWidth()}
                className={!isOnPageLevel ? commonClasses.container : classes.content}
                sx={{ display: 'flex', flexDirection: 'column', alignItems: orientation === Alignment.CENTER ? 'center' : 'flex-start' }}
            >
                <Box display="flex" width="100%" flexDirection="column" alignItems={orientation === Alignment.CENTER ? 'center' : 'flex-start'}>
                    {preTitle &&
                        (preTitleOptions !== PreTitleVariation.MAIN ? (
                            <Box pb={1.5}>
                                <Typography variant={TypographyVariants.overline} color={getTextColorFromTheme(color)}>
                                    {preTitle}
                                </Typography>
                            </Box>
                        ) : (
                            <Box width="100%" display="flex" flexDirection="column" pb={1.5}>
                                <Box display="flex" alignItems="center">
                                    <RectangleIcon
                                        sx={{ color: getPreTitleIconColorFromThemeMode(color), backgroundColor: getPreTitleIconColorFromThemeMode(color), height: '8px', width: '8px', mr: '8px' }}
                                    />
                                    <Typography variant={TypographyVariants.overline} sx={{ color: getTextColorFromTheme(color) }}>
                                        {preTitle}
                                    </Typography>
                                </Box>
                                <Box>
                                    <Divider sx={{ backgroundColor: getPreTitleDividerColorFromThemeMode(color), width: '100%', mt: '16px' }} />
                                </Box>
                            </Box>
                        ))}
                    {title && (
                        <Box pb={1}>
                            <Typography variant={titleTypography} sx={{ textAlign: Alignment.LEFT }} color={getTextColorFromTheme(color)}>
                                {title}
                            </Typography>
                        </Box>
                    )}
                    {description && <RichText textAlign={orientation === Alignment.CENTER ? 'center' : 'start'} pb={3} content={description} sx={{ color: getTextColorFromTheme(color) }} />}
                </Box>
                <MUIGrid container rowSpacing={theme.spacing(7)} columnSpacing={{ xs: 1.75, sm: 1.75, md: 3, lg: 5 }}>
                    {itemSizes
                        ? childComponents.map((child) => (
                              <MUIGrid item xs={xsSize} sm={smSize} md={mdSize} lg={lgSize}>
                                  {child}
                              </MUIGrid>
                          ))
                        : childComponents}
                    {placeholderComponent}
                </MUIGrid>
            </Container>
        </Box>
    );
};

export default Grid;
